import React, { useEffect, useState } from 'react';
import { CheckCircle, ArrowRight, Zap, Loader2 } from 'lucide-react';
import CustomerDashboard from './CustomerDashboard';

const SuccessPage: React.FC = () => {
  const [orderId, setOrderId] = useState<string | null>(null);
  const [showDashboard, setShowDashboard] = useState(false);
  const [isPosting, setIsPosting] = useState(false);
  const [postingResults, setPostingResults] = useState<any>(null);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const orderIdParam = urlParams.get('order_id');
    if (orderIdParam) {
      setOrderId(orderIdParam);
    }

    // Check if there's a pending keyword order to process
    const pendingOrder = localStorage.getItem('pendingKeywordOrder');
    if (pendingOrder) {
      const orderData = JSON.parse(pendingOrder);
      startKeywordPosting(orderData);
    }
  }, []);

  const startKeywordPosting = async (orderData: any) => {
    setIsPosting(true);
    
    try {
      const apiUrl = `${import.meta.env.VITE_API_URL}/api/posting/post-to-sites`;
      console.log('Posting to API URL:', apiUrl);
      console.log('Order data:', orderData);
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          keywords: orderData.keywords,
          domain: orderData.domain,
          businessName: orderData.businessName,
          targetUrl: orderData.targetUrl,
          selectedSites: orderData.selectedSites
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      setPostingResults(result);
      
      // Clear the pending order
      localStorage.removeItem('pendingKeywordOrder');
      
    } catch (error) {
      console.error('Posting error:', error);
      setPostingResults({ success: false, error: 'Failed to post keywords' });
    } finally {
      setIsPosting(false);
    }
  };

  if (showDashboard && orderId) {
    return <CustomerDashboard orderId={orderId} />;
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="max-w-md mx-auto text-center">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Payment Successful!
          </h1>
          
          {isPosting ? (
            <div className="mb-6">
              <div className="flex items-center justify-center gap-2 mb-4">
                <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                <span className="text-blue-600 font-medium">Publishing your keywords...</span>
              </div>
              <p className="text-gray-600 text-sm">
                We're generating AI content and posting to our network of high-authority domains. 
                This may take a few minutes.
              </p>
            </div>
          ) : postingResults ? (
            <div className="mb-6">
              {postingResults.success ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Zap className="w-5 h-5 text-green-600" />
                    <span className="text-green-800 font-medium">Keywords Published Successfully!</span>
                  </div>
                  <p className="text-green-700 text-sm">
                    {postingResults.successCount} keywords posted to {postingResults.successCount} sites
                  </p>
                  {postingResults.errors && postingResults.errors.length > 0 && (
                    <p className="text-orange-600 text-sm mt-2">
                      {postingResults.errorCount} posts failed (will be retried)
                    </p>
                  )}
                </div>
              ) : (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                  <p className="text-red-800 text-sm">
                    Failed to publish keywords. Please contact support.
                  </p>
                </div>
              )}
            </div>
          ) : (
            <p className="text-gray-600 mb-6">
              Your citation campaign has been created and is now being processed. 
              You'll start seeing results within 24-48 hours.
            </p>
          )}

          <div className="space-y-4">
            <button
              onClick={() => setShowDashboard(true)}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
            >
              View Your Dashboard
              <ArrowRight className="w-4 h-4" />
            </button>
            
            <p className="text-sm text-gray-500">
              Bookmark this page to track your campaign progress
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuccessPage;
